/**
 * Tiny KV abstraction. On Capacitor (iOS/Android), prefers @capacitor/preferences
 * (native secure-ish storage). On web/PWA, falls back to localStorage.
 *
 * We dynamic-import the Capacitor module so the web bundle stays clean and
 * Vite never tries to ship native plugin code into the PWA.
 */

const isNative =
  typeof window !== 'undefined' &&
  // Capacitor injects this global on native runtime
  Boolean((window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor);

export async function setItem(key: string, value: string): Promise<void> {
  if (isNative) {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.set({ key, value });
    return;
  }
  localStorage.setItem(key, value);
}

export async function getItem(key: string): Promise<string | null> {
  if (isNative) {
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key });
    return value;
  }
  return localStorage.getItem(key);
}

export async function removeItem(key: string): Promise<void> {
  if (isNative) {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.remove({ key });
    return;
  }
  localStorage.removeItem(key);
}

export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'gpl.auth.access',
  REFRESH_TOKEN: 'gpl.auth.refresh',
  USER: 'gpl.auth.user',
  TENANT: 'gpl.auth.tenant',
  BIOMETRIC_ENABLED: 'gpl.biometric.enabled',
  PUSH_TOKEN: 'gpl.push.token',
  PUSH_OPTIN: 'gpl.push.optin',
} as const;
